import { Link } from 'react-router-dom';
import { categories, Category } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo } from '@/mocks/siteData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState<string>(slug || 'yingshi');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory) || categories[0];
  const categoryArticles = articles.filter((a) => a.category === activeCategory);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - ${siteInfo.name}`,
    description: currentCategory.description,
    url: `${siteInfo.url}/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]}
            />
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mt-2">
              {currentCategory.name}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              {currentCategory.description} — 共 {currentCategory.articleCount} 篇资源
            </p>
          </div>
        </div>

        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat: Category) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700'
                }`}
              >
                <i className={`${cat.icon} mr-1.5`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categoryArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group rounded-xl overflow-hidden bg-background-50 border border-background-200 hover:border-primary-200 transition-all cursor-pointer"
              >
                <div className="relative w-full h-48 overflow-hidden">
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <h3 className="font-heading text-base text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-500 line-clamp-2 mb-3">
                    {article.description}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <span>{article.publishDate}</span>
                    <span>{article.readTime}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {categoryArticles.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-background-200 text-foreground-400 mb-4">
                <i className="ri-inbox-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无文章，请浏览其他分类</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}